import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Link, Prompt, useHistory, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { UserContext } from 'globalContext/UserContext';
import moment from 'moment';
import ControlledDatePicker from 'components/ControlledDatePicker';
import ControlledSelect from 'components/ControlledSelect/ControlledSelect';
import ControlledTextField from 'components/ControlledTextField/ControlledTextField';
import {
  createOrder,
  fetchOrder,
  fetchOrderReferenceData,
  renewOrder,
} from './api';
import OrderAccess from './OrderAccess';
import OrderCodeField from './OrderCodeField';
import OrderSubjectField from './OrderSubjectField';
import PageHeader from './PageHeader';
import { IssuedOrderState, OrderDetails, SelectOption } from './types';
import {
  consultantName,
  getActionErrorDetail,
  getActionErrorMessage,
  getDefaultDecisionDeadline,
  getDefaultIssueDates,
  getEarliestStartDate,
  getLatestDecisionDeadline,
  getOverlappingOrderNumber,
  getRenewalFormValues,
  isOrderRenewable,
  ORDER_CONDITIONS_TEMPLATE,
  OrderFormErrors,
  OrderFormValues,
  toOrderCreateRequest,
  toOrderRenewRequest,
  validateOrderForm,
  validateRenewalForm,
} from './utils';
import * as Styles from './Orders.css';

export const OrderFormContent = ({
  renewal = false,
}: {
  renewal?: boolean;
}) => {
  const intl = useIntl();
  const history = useHistory();
  const { username } = useContext(UserContext);
  const { orderNumber } = useParams<{ orderNumber?: string }>();
  const defaultValues: OrderFormValues = {
    consultant: null,
    client: null,
    owner: null,
    startDate: null,
    endDate: null,
    decisionDeadline: null,
    subject: '',
    // A new assignment opens with the conditions template (P3); an
    // extension gets the predecessor's conditions once it is loaded.
    conditions: renewal ? '' : ORDER_CONDITIONS_TEMPLATE,
    notifyAlso: [],
  };
  const { control, formState, getValues, reset, setValue, watch } =
    useForm<OrderFormValues>({ defaultValues });
  const resetRef = useRef(reset);
  // The decision deadline follows the start until the issuer edits it.
  const deadlineTouchedRef = useRef(false);
  const startDate = watch('startDate');
  const validStart =
    startDate && moment(startDate).isValid() ? moment(startDate) : undefined;
  // Change request of 30.10: the previous month stays open until its
  // settlement, so an assignment may still start there.
  const earliestStart = getEarliestStartDate();
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  // Active accounts only: an inactive one cannot get an assignment (B7), nor
  // can an employment-contract employee (change request of 26.10, P2).
  const [consultants, setConsultants] = useState<SelectOption[]>([]);
  // Only people who manage or view all assignments (D2).
  const [notifyCandidates, setNotifyCandidates] = useState<SelectOption[]>([]);
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [loadingReferences, setLoadingReferences] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<OrderFormErrors>({});
  const [requestError, setRequestError] = useState('');
  const [requestErrorDetail, setRequestErrorDetail] = useState('');
  // The assignment this one overlaps, shown at the consultant and dates;
  // '' when the backend names none.
  const [overlap, setOverlap] = useState<string | null>(null);
  const [predecessor, setPredecessor] = useState<OrderDetails | null>(null);
  const [initialLoadError, setInitialLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [orderCode, setOrderCode] = useState('');
  const [initialOrderCode, setInitialOrderCode] = useState('');
  // Set right before leaving after a successful submit, so the unsaved
  // changes prompt lets that navigation through.
  const submittedRef = useRef(false);
  // The assignment code lives outside the form, so it counts separately.
  const dirty = formState.isDirty || orderCode !== initialOrderCode;

  useEffect(() => {
    setLoadingReferences(true);
    setRequestError('');
    setRequestErrorDetail('');
    setInitialLoadError(false);
    deadlineTouchedRef.current = false;
    Promise.all([
      fetchOrderReferenceData({
        activeConsultants: !renewal,
        notifyCandidates: true,
      }),
      renewal && orderNumber ? fetchOrder(orderNumber) : Promise.resolve(null),
    ]).then(([references, orderResult]) => {
      if (!references.ok) {
        setRequestError('ORDERS.REFERENCE_ERROR');
        setInitialLoadError(true);
        setLoadingReferences(false);
        return;
      }
      setEmployees(references.employees);
      setConsultants(references.activeEmployees);
      setNotifyCandidates(references.notifyCandidates);
      setClients(references.activeClients);

      if (renewal && orderResult) {
        if (!orderResult.ok || !orderResult.data) {
          setRequestError(
            orderResult.status === 401 || orderResult.status === 403
              ? 'ORDERS.ACTION_ERROR.UNAUTHORIZED'
              : 'ORDERS.RENEW_LOAD_ERROR',
          );
          setInitialLoadError(true);
          setLoadingReferences(false);
          return;
        }
        const loaded = orderResult.data;
        const renewalValues = getRenewalFormValues(
          loaded,
          references.employees,
          references.activeClients,
          references.notifyCandidates,
        );
        setPredecessor(loaded);
        setOrderCode(renewalValues.orderCode || '');
        setInitialOrderCode(renewalValues.orderCode || '');
        resetRef.current(renewalValues);
      } else if (!renewal) {
        const ownerId = references.employeeIdsByUsername[username];
        resetRef.current({
          // The fields are not mounted while the references load, so
          // getValues() lacks them; keep the defaults (the conditions
          // template) underneath.
          ...defaultValues,
          ...getValues(),
          // An undefined value makes the date picker show today; keep the end
          // date empty until the owner picks one.
          endDate: getValues().endDate ?? null,
          ...getDefaultIssueDates(),
          owner:
            references.employees.find(({ value }) => value === ownerId) || null,
        });
      }
      setLoadingReferences(false);
    });
    // Defaults are applied once per load; getValues is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAttempt, orderNumber, renewal, username]);

  const translateError = (key: keyof OrderFormValues) =>
    errors[key] ? intl.formatMessage({ id: errors[key] }) : undefined;

  // Only accepted, completed or ended-early orders without a successor can
  // be renewed; the backend answers 409 otherwise.
  const renewalBlocked =
    renewal && predecessor !== null && !isOrderRenewable(predecessor.order);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (renewal && (!orderNumber || !predecessor)) {
      setRequestError('ORDERS.RENEW_LOAD_ERROR');
      setInitialLoadError(true);
      return;
    }
    if (renewalBlocked) {
      return;
    }
    const values: OrderFormValues = { ...getValues(), orderCode };
    const validationErrors =
      renewal && predecessor
        ? validateRenewalForm(
            values,
            predecessor.order,
            undefined,
            predecessor.conditions,
          )
        : validateOrderForm(values);
    setErrors(validationErrors);
    setRequestError('');
    setRequestErrorDetail('');
    setOverlap(null);
    if (Object.keys(validationErrors).length) {
      return;
    }

    setSubmitting(true);
    const result =
      renewal && orderNumber
        ? await renewOrder(orderNumber, toOrderRenewRequest(values))
        : await createOrder(toOrderCreateRequest(values));
    setSubmitting(false);
    if (result.ok && result.data) {
      const { order } = result.data;
      const state: IssuedOrderState = {
        issuedOrderNumber: order.orderNumber,
        extension: renewal,
      };
      submittedRef.current = true;
      history.push({
        pathname: '/assignments',
        search: `?tab=all&ownerId=${order.ownerId}`,
        state,
      });
      return;
    }
    const overlapping = getOverlappingOrderNumber(result);
    const knownActionError = [400, 401, 403, 409].includes(result.status);
    if (overlapping !== null) {
      setOverlap(overlapping);
    } else if (knownActionError) {
      setRequestError(getActionErrorMessage(result.status));
      setRequestErrorDetail(
        getActionErrorDetail(result.status, result.message),
      );
    } else {
      setRequestError(renewal ? 'ORDERS.RENEW_ERROR' : 'ORDERS.CREATE_ERROR');
    }
  };

  return (
    <Styles.Page>
      <Prompt
        when={dirty}
        message={() =>
          submittedRef.current ||
          intl.formatMessage({ id: 'ORDERS.DISCARD_CHANGES' })
        }
      />
      <Styles.FormPaper>
        <PageHeader
          title={intl.formatMessage({
            id: renewal ? 'ORDERS.RENEW_TITLE' : 'ORDERS.CREATE',
          })}
          description={intl.formatMessage(
            {
              id: renewal
                ? 'ORDERS.RENEW_DESCRIPTION'
                : 'ORDERS.CREATE_DESCRIPTION',
            },
            { orderNumber: predecessor?.order.orderNumber || orderNumber },
          )}
        />

        {renewalBlocked && (
          <Box mt={2}>
            <Alert severity="warning">
              {intl.formatMessage({ id: 'ORDERS.RENEW_NOT_ALLOWED' })}
            </Alert>
          </Box>
        )}

        {requestError && (
          <Box mt={2}>
            <Alert severity="error">
              {requestError.startsWith('ORDERS.')
                ? intl.formatMessage({ id: requestError })
                : requestError}
              {requestErrorDetail && <div>{requestErrorDetail}</div>}
            </Alert>
          </Box>
        )}

        {loadingReferences && (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        )}

        {!loadingReferences && initialLoadError && (
          <Box display="flex" justifyContent="center" p={3}>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setLoadAttempt((attempt) => attempt + 1)}
            >
              {intl.formatMessage({ id: 'ORDERS.RETRY' })}
            </Button>
          </Box>
        )}

        {!loadingReferences && !initialLoadError && (
          <form onSubmit={submit} noValidate>
            <Styles.FormGrid>
              <div>
                <ControlledSelect
                  control={control}
                  name="consultant"
                  label="ORDERS.CONSULTANT"
                  helperText
                  options={
                    renewal
                      ? employees
                      : consultants.map((option) => ({
                          ...option,
                          name: consultantName(
                            intl,
                            option.name,
                            option.awaitingFirstSignIn,
                          ),
                        }))
                  }
                  errorMessage={errors.consultant}
                  isDisabled={renewal}
                  required
                />
              </div>
              <div>
                <ControlledSelect
                  control={control}
                  name="client"
                  label="ORDERS.CLIENT"
                  helperText
                  options={clients}
                  errorMessage={errors.client}
                  isDisabled={renewal}
                  required
                />
              </div>
              {renewal && (
                <Styles.FullWidth>
                  <Typography variant="caption" color="textSecondary">
                    {intl.formatMessage({ id: 'ORDERS.RENEW_LOCKED_HELP' })}
                  </Typography>
                </Styles.FullWidth>
              )}
              <div>
                <ControlledSelect
                  control={control}
                  name="owner"
                  label="ORDERS.OWNER"
                  helperText
                  options={employees}
                  errorMessage={errors.owner}
                  required
                />
              </div>
              <div>
                <ControlledDatePicker
                  control={control}
                  name="startDate"
                  label={intl.formatMessage({ id: 'ORDERS.START_DATE' })}
                  errorMessage={translateError('startDate')}
                  minDate={earliestStart}
                  clearable
                  view="date"
                  onChange={(date) => {
                    if (
                      !deadlineTouchedRef.current &&
                      date &&
                      moment(date).isValid()
                    ) {
                      setValue(
                        'decisionDeadline',
                        getDefaultDecisionDeadline(date),
                      );
                    }
                  }}
                />
              </div>
              <div>
                <ControlledDatePicker
                  control={control}
                  name="endDate"
                  label={intl.formatMessage({ id: 'ORDERS.END_DATE' })}
                  errorMessage={translateError('endDate')}
                  minDate={validStart || earliestStart}
                  clearable
                  view="date"
                />
              </div>
              <div>
                <ControlledDatePicker
                  control={control}
                  name="decisionDeadline"
                  label={intl.formatMessage({ id: 'ORDERS.DEADLINE' })}
                  errorMessage={translateError('decisionDeadline')}
                  minDate={moment()}
                  maxDate={validStart && getLatestDecisionDeadline(validStart)}
                  disablePast
                  clearable
                  view="date"
                  onChange={() => {
                    deadlineTouchedRef.current = true;
                  }}
                />
              </div>
              {overlap !== null && (
                <Styles.FullWidth>
                  <Alert severity="error" role="alert">
                    {overlap
                      ? intl.formatMessage(
                          { id: 'ORDERS.OVERLAP' },
                          {
                            number: (
                              <Link to={`/assignments/${overlap}`}>
                                {overlap}
                              </Link>
                            ),
                          },
                        )
                      : intl.formatMessage({ id: 'ORDERS.OVERLAP_UNKNOWN' })}
                    <div>
                      {intl.formatMessage({ id: 'ORDERS.OVERLAP_HELP' })}
                    </div>
                  </Alert>
                </Styles.FullWidth>
              )}
              <div>
                <OrderCodeField
                  id="order-code"
                  value={orderCode}
                  onChange={(value) => {
                    setOrderCode(value);
                    setErrors((current) => ({
                      ...current,
                      orderCode: undefined,
                    }));
                  }}
                  error={errors.orderCode}
                />
              </div>
              <Styles.FullWidth>
                <ControlledSelect
                  control={control}
                  name="notifyAlso"
                  label="ORDERS.NOTIFY_ALSO"
                  helperText
                  isMulti
                  options={notifyCandidates}
                />
                <Typography variant="caption" color="textSecondary">
                  {intl.formatMessage({ id: 'ORDERS.NOTIFY_ALSO_HELP' })}
                </Typography>
              </Styles.FullWidth>
              <Styles.FullWidth>
                <Controller
                  control={control}
                  name="subject"
                  render={({ value, onChange }) => (
                    <OrderSubjectField
                      id="order-subject"
                      value={value || ''}
                      onChange={(next) => {
                        onChange(next);
                        setErrors((current) => ({
                          ...current,
                          subject: undefined,
                        }));
                      }}
                      error={errors.subject}
                    />
                  )}
                />
              </Styles.FullWidth>
              <Styles.FullWidth>
                <ControlledTextField
                  control={control}
                  name="conditions"
                  label="ORDERS.CONDITIONS"
                  variant="outlined"
                  isMulti
                  rows={8}
                  maxLength={10000}
                  required
                  validate={Boolean(errors.conditions)}
                  rules={errors.conditions ? {} : undefined}
                  helperText={
                    translateError('conditions') ||
                    intl.formatMessage({ id: 'ORDERS.CONDITIONS_HELP' })
                  }
                />
              </Styles.FullWidth>
            </Styles.FormGrid>
            <Box display="flex" justifyContent="flex-end" gridGap={12}>
              <Button
                onClick={() =>
                  history.push(
                    renewal && orderNumber
                      ? `/assignments/${orderNumber}`
                      : '/assignments',
                  )
                }
                disabled={submitting}
              >
                {intl.formatMessage({ id: 'ORDERS.CANCEL' })}
              </Button>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                disabled={submitting || renewalBlocked}
              >
                {submitting ? (
                  <CircularProgress size={22} />
                ) : (
                  intl.formatMessage({
                    id: renewal ? 'ORDERS.RENEW_SUBMIT' : 'ORDERS.ISSUE',
                  })
                )}
              </Button>
            </Box>
          </form>
        )}
      </Styles.FormPaper>
    </Styles.Page>
  );
};

const OrderForm = () => (
  <OrderAccess requireManage>{() => <OrderFormContent />}</OrderAccess>
);

export default OrderForm;

export const OrderRenewalForm = () => (
  <OrderAccess requireManage>{() => <OrderFormContent renewal />}</OrderAccess>
);
